import { CreateGenerateTestSetJobViewModel, TestSetGenerationMode, useGetApiEmbeddingCollectionList, usePostApiGenerateTestSetJobPreview } from "@/API";
import {
    ActionIcon,
    Badge,
    Button,
    Card,
    Divider,
    Group,
    Loader,
    Modal,
    MultiSelect,
    NumberInput,
    Radio,
    Slider,
    Stack,
    Text,
    Textarea,
    TextInput,
    Tooltip
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconFileCheck, IconInfoCircle } from "@tabler/icons-react";
import { PropsWithChildren, useEffect } from "react";
import { SelectDataIdsModal } from "./SelectDataIdsModal";

const SYNTHESIZER_INFO = [
    {
        key: "SingleHop",
        label: "單跳事實題 (SingleHop)",
        shortDesc: "基於單一內文段落進行直接問答",
        tooltip: "隨機挑選單一內文段落，產生明確的直接問答題目，主要用於測試檢索與基本回答能力。"
    },
    {
        key: "MultiHop",
        label: "多跳推理題 (MultiHop)",
        shortDesc: "利用 Qdrant 向量關聯檢索兩段 Context 進行跨段推理",
        tooltip: "透過 Qdrant 密集/稀疏混合向量檢索找出語意相關的兩段 Context，產生需結合兩處事實才能回答的跨段推理題目。"
    },
    {
        key: "Comparative",
        label: "對比比較題 (Comparative)",
        shortDesc: "利用 Qdrant 搜尋具關聯但屬於不同段落的主題進行對比",
        tooltip: "透過 Qdrant 向量搜尋具語意關聯但屬於不同段落的主題，生成針對特點、規範或條款進行比較對照的題目。"
    },
    {
        key: "Abstractive",
        label: "高階綜述題 (Abstractive)",
        shortDesc: "組合多個段落，生成整體觀念與要點綜述",
        tooltip: "檢索並組合多個 Context 段落，生成針對全貌主題、核心要點或整體觀念進行概括說明的綜述題目。"
    }
];

export function CreateGenerateTestSetJobButton(props: { workspaceId: string, testSetId?: string, onSubmit?: (value: CreateGenerateTestSetJobViewModel) => void } & PropsWithChildren) {
    const [opened, { open, close }] = useDisclosure(false);
    const [dataIdModalOpened, { open: openDataIdModal, close: closeDataIdModal }] = useDisclosure(false);

    const embeddingCollection = useGetApiEmbeddingCollectionList({ workspaceId: props.workspaceId });
    const previewMutation = usePostApiGenerateTestSetJobPreview();

    const form = useForm<CreateGenerateTestSetJobViewModel>({
        initialValues: {
            testSetId: props.testSetId ?? "",
            testSetSize: 5,
            generationMode: TestSetGenerationMode.ChunkCoverage,
            targetTotalSize: 10,
            densityRatio: 10,
            selectedDataIds: [],
            persona: {
                role_description: "",
                name: ""
            },
            embeddingCollectionIds: [],
            synthesizerDistribution: {
                SingleHop: 30,
                MultiHop: 30,
                Comparative: 20,
                Abstractive: 20
            }
        },
        validate: {
            embeddingCollectionIds: (value, values) => {
                if (values.generationMode !== TestSetGenerationMode.SelectedDataIds && (!value || value.length === 0)) {
                    return "請至少選擇一個資料集合";
                }
                return null;
            },
            selectedDataIds: (value, values) => {
                if (values.generationMode === TestSetGenerationMode.SelectedDataIds && (!value || value.length === 0)) {
                    return "請至少選擇一個檔案";
                }
                return null;
            }
        }
    });

    useEffect(() => {
        if (opened) form.reset();
    }, [opened]);

    useEffect(() => {
        if (!opened) return;

        const timer = setTimeout(() => {
            previewMutation.mutate({
                params: { workspaceId: props.workspaceId },
                data: {
                    ...form.values,
                    testSetId: props.testSetId ?? form.values.testSetId ?? ""
                }
            });
        }, 300);
        return () => clearTimeout(timer);
    }, [opened, props.workspaceId, props.testSetId, JSON.stringify(form.values)]);

    const dist = form.values.synthesizerDistribution || { SingleHop: 30, MultiHop: 30, Comparative: 20, Abstractive: 20 };
    const totalWeight = Object.values(dist).reduce((acc, curr) => acc + (curr || 0), 0) || 1;

    const allCollections = embeddingCollection.data?.data.results ?? [];
    const mode = form.values.generationMode ?? TestSetGenerationMode.ChunkCoverage;

    return <>
        <div onClick={open}>
            {props.children}
        </div>
        <Modal opened={opened} onClose={close} title="產生測試資料" size="lg">
            <form onSubmit={form.onSubmit((values) => {
                console.log(values);
                props.onSubmit?.(values);
                close();
            })}>
                <Stack gap="md">
                    <Radio.Group
                        label="選擇出題模式"
                        description="依據您的資料型態，選擇最適切的出題策略"
                        value={mode}
                        onChange={(val) => form.setFieldValue('generationMode', val as TestSetGenerationMode)}
                    >
                        <Stack gap="xs" mt="xs">
                            <Radio
                                value={TestSetGenerationMode.ChunkCoverage}
                                label="廣度覆蓋模式 (Chunk Coverage)"
                                description="跨集合與檔案 (DataId) 全局抽樣合成，可能會產生跨 DataId 的綜合題目；處理紀錄明細中的產出題數將按題目引用的 DataId 分別計數。"
                            />
                            <Radio
                                value={TestSetGenerationMode.Proportional}
                                label="按資料量比例模式 (Proportional)"
                                description="依據各文件/DataId 在向量庫中的 Chunk 資料量佔比分配題數，大文件多出題、小文件少出題。"
                            />
                            <Radio
                                value={TestSetGenerationMode.SelectedDataIds}
                                label="自訂特定文件檔案模式 (Selected DataIds)"
                                description="開啟彈窗分頁與關鍵字搜尋，獨立挑選特定的文件檔案 (DataId) 進行出題。"
                            />
                            <Radio
                                value={TestSetGenerationMode.DensityRatio}
                                label="按資料密度抽樣模式 (Density Ratio)"
                                description="設定『每 N 個 Chunk 抽樣 1 題』，系統依據資料量密度自動計算產出總題數。"
                            />
                        </Stack>
                    </Radio.Group>

                    <Divider label="資料來源與規模設定" labelPosition="center" my="xs" />

                    {/* Mode Specific Inputs */}
                    {mode !== TestSetGenerationMode.SelectedDataIds && (
                        <Stack gap="md">
                            <MultiSelect
                                label="資料集合 (Embedding Collections)"
                                description="選取欲涵蓋的資料集合 (請至少選擇一個資料集合)"
                                withAsterisk
                                searchable
                                clearable
                                placeholder="請選擇資料集合..."
                                data={allCollections.filter(x => !!x.id).map(x => ({
                                    label: x.name ?? "",
                                    value: x.id ?? ""
                                }))}
                                {...form.getInputProps('embeddingCollectionIds')}
                            />

                            {mode === TestSetGenerationMode.ChunkCoverage && (
                                <NumberInput
                                    label="目標總題目數 (TargetTotalSize)"
                                    description="跨所有選定集合與檔案 (DataId) 全局抽樣合成的總題目個數"
                                    min={1}
                                    max={100}
                                    {...form.getInputProps('targetTotalSize')}
                                />
                            )}

                            {mode === TestSetGenerationMode.Proportional && (
                                <NumberInput
                                    label="目標總題目數 (TargetTotalSize)"
                                    description="依各文件 Chunk 數量佔比分配後的總題目個數"
                                    min={1}
                                    max={100}
                                    {...form.getInputProps('targetTotalSize')}
                                />
                            )}

                            {mode === TestSetGenerationMode.DensityRatio && (
                                <NumberInput
                                    label="抽樣密度 (Density Ratio)"
                                    description="代表『每 N 個 Chunk 產出 1 題』。例如設為 10，則 100 個 Chunk 的文件將自動產生 10 題。"
                                    min={1}
                                    max={100}
                                    {...form.getInputProps('densityRatio')}
                                />
                            )}
                        </Stack>
                    )}

                    {mode === TestSetGenerationMode.SelectedDataIds && (
                        <Stack gap="md">
                            <Card withBorder radius="md" p="sm">
                                <Group justify="space-between" align="center">
                                    <Stack gap={2}>
                                        <Group gap={6}>
                                            <IconFileCheck size={18} color="var(--mantine-color-blue-6)" />
                                            <Text size="sm" fw={600}>指定特定文件檔案 (DataIds)</Text>
                                        </Group>
                                        <Text size="xs" c="dimmed">
                                            {form.values.selectedDataIds && form.values.selectedDataIds.length > 0
                                                ? `已選擇 ${form.values.selectedDataIds.length} 個特定檔案`
                                                : "尚未選擇特定檔案 (將開啟彈窗選取檔案)"}
                                        </Text>
                                    </Stack>
                                    <Button variant="light" size="xs" onClick={openDataIdModal}>
                                        {form.values.selectedDataIds && form.values.selectedDataIds.length > 0
                                            ? "修改選擇 (開啟彈窗)"
                                            : "開啟檔案選擇彈窗 (含分頁與搜尋)"}
                                    </Button>
                                </Group>
                            </Card>

                            <NumberInput
                                label="每份勾選檔案產出的題目數"
                                description="針對彈窗中所勾選的每份文件，分別產生的題目數量"
                                min={1}
                                max={100}
                                {...form.getInputProps('targetTotalSize')}
                            />
                        </Stack>
                    )}

                    <Divider label="題型合成器配比權重 (Synthesizer Distribution)" labelPosition="center" my="xs" />

                    <Card withBorder radius="md" p="sm" bg="var(--mantine-color-body)">
                        <Stack gap="sm">
                            <Text size="xs" c="dimmed">
                                設定不同出題合成器的相對權重。系統將依據權重比例調配合成出的各題型數量。
                            </Text>

                            {SYNTHESIZER_INFO.map(item => {
                                const currentWeight = (dist as Record<string, number>)[item.key] ?? 0;
                                const percentage = Math.round((currentWeight / totalWeight) * 100);

                                return (
                                    <Stack key={item.key} gap={4}>
                                        <Group justify="space-between">
                                            <Group gap={6}>
                                                <Text size="sm" fw={500}>
                                                    {item.label}
                                                </Text>
                                                <Tooltip multiline w={280} label={item.tooltip} withArrow position="right">
                                                    <ActionIcon variant="subtle" color="gray" size="xs">
                                                        <IconInfoCircle size={14} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            </Group>
                                            <Badge variant="light" size="sm">
                                                權重 {currentWeight} ({percentage}%)
                                            </Badge>
                                        </Group>

                                        <Text size="xs" c="dimmed">
                                            {item.shortDesc}
                                        </Text>

                                        <Group gap="sm">
                                            <Slider
                                                flex={1}
                                                min={0}
                                                max={100}
                                                step={5}
                                                value={currentWeight}
                                                onChange={(val) => {
                                                    form.setFieldValue(`synthesizerDistribution.${item.key}`, val);
                                                }}
                                            />
                                            <NumberInput
                                                w={75}
                                                size="xs"
                                                min={0}
                                                max={100}
                                                value={currentWeight}
                                                onChange={(val) => {
                                                    form.setFieldValue(`synthesizerDistribution.${item.key}`, Number(val) || 0);
                                                }}
                                            />
                                        </Group>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </Card>

                    <Divider label="角色設定 (Persona)" labelPosition="center" my="xs" />

                    <TextInput
                        label="角色名稱"
                        description="用來決定產生題目的口吻、思考模式等等"
                        placeholder="使用者"
                        {...form.getInputProps('persona.name')}
                    />

                    <Textarea
                        autosize
                        label="角色描述"
                        description="需要和角色名稱一同設定，其中一項為空則使用預設"
                        placeholder="一般使用者，只會使用繁體中文，不會使用任何其他語言。"
                        {...form.getInputProps('persona.role_description')}
                    />

                    <Divider label="預估出題結果 (Preview)" labelPosition="center" my="xs" />

                    <Card withBorder radius="md" p="md" bg="var(--mantine-color-blue-light)">
                        <Stack gap="xs">
                            <Group justify="space-between" align="center">
                                <div>
                                    <Text size="xs" c="dimmed" fw={500}>預計產出總題目數</Text>
                                    <Group gap="xs" align="baseline">
                                        {previewMutation.isPending ? (
                                            <Loader size="xs" />
                                        ) : (
                                            <Text size="xl" fw={700} c="blue">
                                                {previewMutation.data?.data.estimatedTotalQuestions ?? 0} 題
                                            </Text>
                                        )}
                                        {previewMutation.data?.data.totalChunks !== undefined && (
                                            <Text size="xs" c="dimmed">
                                                (涵蓋 {previewMutation.data?.data.totalChunks} 個 Chunk)
                                            </Text>
                                        )}
                                    </Group>
                                </div>
                            </Group>

                            {previewMutation.data?.data.synthesizerBreakdown && (
                                <Group gap="xs">
                                    {Object.entries(previewMutation.data.data.synthesizerBreakdown).map(([key, count]) => {
                                        const labelMap: Record<string, string> = {
                                            SingleHop: "單跳事實",
                                            MultiHop: "多跳推理",
                                            Comparative: "對比比較",
                                            Abstractive: "高階綜述"
                                        };
                                        return (
                                            <Badge key={key} variant="white" color="blue" size="sm">
                                                {labelMap[key] ?? key}: {count} 題
                                            </Badge>
                                        );
                                    })}
                                </Group>
                            )}
                        </Stack>
                    </Card>

                    <Group justify="flex-end" mt="md">
                        <Button type="submit">確認產生</Button>
                    </Group>
                </Stack>
            </form>

            {/* Modal Popup for selecting specific DataIds with pagination & search */}
            <SelectDataIdsModal
                opened={dataIdModalOpened}
                onClose={closeDataIdModal}
                workspaceId={props.workspaceId}
                collectionIds={form.values.embeddingCollectionIds}
                selectedDataIds={form.values.selectedDataIds ?? []}
                onConfirm={(newSelectedDataIds, inferredColIds) => {
                    form.setFieldValue('selectedDataIds', newSelectedDataIds);
                    if (inferredColIds.length > 0) {
                        const existingCols = new Set(form.values.embeddingCollectionIds || []);
                        inferredColIds.forEach(id => existingCols.add(id));
                        form.setFieldValue('embeddingCollectionIds', Array.from(existingCols));
                    }
                }}
            />
        </Modal>
    </>;
}
